// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { SegmentBenchmark } from '@/billing/lifecycle/benchmarks';
import { formatDays } from '@/billing/lifecycle/date-helpers';
import {
  Box,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';

// Card-level indicator dimensions. The on-card visual is a thin
// zone-tinted bar with a small triangle marker above it; the full
// scale (ticks, labels, anchors) lives inside the hover tooltip so
// the card stays visually anchored to the headline metric.
const BAR_HEIGHT_PX = 5;
const MARKER_HEIGHT_PX = 6;
const MARKER_HALF_WIDTH_PX = 4;
const MARKER_GAP_PX = 1;
const INDICATOR_HEIGHT_PX = MARKER_HEIGHT_PX + MARKER_GAP_PX + BAR_HEIGHT_PX;

// Tooltip-internal scale dimensions (the visual moved off the card).
const TOOLTIP_POINTER_HEIGHT_PX = 12;
const TOOLTIP_POINTER_WIDTH_PX = 2;
const TOOLTIP_POINTER_DOT_DIAMETER_PX = 6;
const TOOLTIP_TRACK_HEIGHT_PX = 4;
const TOOLTIP_TICK_HEIGHT_PX = 8;
const TOOLTIP_TICK_WIDTH_PX = 1.5;
const TOOLTIP_SCALE_HEIGHT_PX = 36;
const TOOLTIP_TRACK_TOP_GAP_PX = 2;
const TOOLTIP_TICK_LABEL_TOP_GAP_PX = 2;
const TOOLTIP_TICK_LABEL_BAND_HEIGHT_PX = 14;
const TOOLTIP_EARLY_ANNOTATION_OFFSET_PX = 4;
const TOOLTIP_PINNED_CARET_FONT_PX = 14;
const TOOLTIP_MIN_WIDTH_PX = 220;
const TOOLTIP_MAX_WIDTH_PX = 280;
const CARD_PINNED_CARET_FONT_PX = 12;

// Axis-edge buffer beyond `concerning`. The axis runs from 0 to
// `concerning × AXIS_OVERFLOW_FACTOR` so a value at exactly the
// concerning threshold sits visually inside the bar with a bit of
// breathing room before pinning to the axis edge.
const AXIS_OVERFLOW_FACTOR = 1.3;

// Caret indicator rendered when the org's value exceeds a visible
// edge of the axis. Positions slightly inset from the edge so it
// reads as a "more than this" marker rather than a clipped value.
const PINNED_INSET_PCT = 1.5;

// Tint strength applied to zone segment fills. The bar reads as soft
// context behind the marker, not a vibrant chart in its own right.
const ZONE_FILL_ALPHA = 0.3;

interface LifecycleBenchmarkIndicatorProps {
  // Average days for the org's segment, or null when the segment has
  // no observations in the rolling window. Null renders the empty
  // bar (no marker) so the card communicates "scale exists, no
  // datapoint yet" without flickering through a fake value.
  value: number | null;
  benchmark: SegmentBenchmark;
  isLoading?: boolean;
}

type ZonePaletteKey = 'success' | 'warning' | 'error' | 'neutral';

interface ZoneSegment {
  leftPct: number;
  rightPct: number;
  paletteKey: ZonePaletteKey;
}

// Resolve the marker color based on which zone the org's value falls
// into. Two-sided benchmarks compare against the absolute value so
// future-dated and back-dated date lag both surface the same warning.
// Negative org values on one-sided segments are not zone-mapped — the
// caller renders them at the left edge in a muted color.
function resolvePointerColor(
  value: number,
  benchmark: SegmentBenchmark
): string {
  const magnitude = benchmark.signed ? Math.abs(value) : value;
  if (magnitude < 0) return 'text.secondary';
  if (magnitude <= benchmark.bestInClass) return 'success.main';
  if (magnitude <= benchmark.industryMedian) return 'text.primary';
  if (magnitude <= benchmark.concerning) return 'warning.main';
  return 'error.main';
}

// Compute the on-axis position of a given day value as a left-edge
// percent. Returns either a percent in [0, 100] or a pinned sentinel
// when the value exceeds the visible axis end.
function computePointerPosition(
  value: number,
  benchmark: SegmentBenchmark
):
  | { kind: 'inside'; leftPct: number }
  | { kind: 'pinned-left' }
  | { kind: 'pinned-right' } {
  const axisMax = benchmark.concerning * AXIS_OVERFLOW_FACTOR;

  if (benchmark.signed) {
    if (value < -axisMax) {
      return { kind: 'pinned-left' };
    }
    if (value > axisMax) {
      return { kind: 'pinned-right' };
    }
    const leftPct = 50 + (value / axisMax) * 50;
    return { kind: 'inside', leftPct };
  }

  if (value < 0) {
    return { kind: 'inside', leftPct: 0 };
  }
  if (value > axisMax) {
    return { kind: 'pinned-right' };
  }
  const leftPct = (value / axisMax) * 100;
  return { kind: 'inside', leftPct };
}

// Compute zone-tinted bar segments. One-sided benchmarks produce four
// segments (best / typical / above-median / concerning); two-sided
// benchmarks mirror those zones around 0.
function computeZoneSegments(benchmark: SegmentBenchmark): ZoneSegment[] {
  const axisMax = benchmark.concerning * AXIS_OVERFLOW_FACTOR;

  if (benchmark.signed) {
    const bestPct = (benchmark.bestInClass / axisMax) * 50;
    const medianPct = (benchmark.industryMedian / axisMax) * 50;
    const concerningPct = (benchmark.concerning / axisMax) * 50;
    return [
      { leftPct: 0, rightPct: 50 - concerningPct, paletteKey: 'error' },
      {
        leftPct: 50 - concerningPct,
        rightPct: 50 - medianPct,
        paletteKey: 'warning',
      },
      {
        leftPct: 50 - medianPct,
        rightPct: 50 - bestPct,
        paletteKey: 'neutral',
      },
      {
        leftPct: 50 - bestPct,
        rightPct: 50 + bestPct,
        paletteKey: 'success',
      },
      {
        leftPct: 50 + bestPct,
        rightPct: 50 + medianPct,
        paletteKey: 'neutral',
      },
      {
        leftPct: 50 + medianPct,
        rightPct: 50 + concerningPct,
        paletteKey: 'warning',
      },
      { leftPct: 50 + concerningPct, rightPct: 100, paletteKey: 'error' },
    ];
  }

  const bestPct = (benchmark.bestInClass / axisMax) * 100;
  const medianPct = (benchmark.industryMedian / axisMax) * 100;
  const concerningPct = (benchmark.concerning / axisMax) * 100;
  return [
    { leftPct: 0, rightPct: bestPct, paletteKey: 'success' },
    { leftPct: bestPct, rightPct: medianPct, paletteKey: 'neutral' },
    { leftPct: medianPct, rightPct: concerningPct, paletteKey: 'warning' },
    { leftPct: concerningPct, rightPct: 100, paletteKey: 'error' },
  ];
}

// Compute tick positions on the tooltip-internal axis as left-edge
// percentages. For one-sided benchmarks: three ticks (best, median,
// concerning). For two-sided benchmarks: 0 sits at 50% with mirrored
// magnitude ticks on each side.
function computeAnchorTicks(
  benchmark: SegmentBenchmark
): { leftPct: number; label: string }[] {
  const axisMax = benchmark.concerning * AXIS_OVERFLOW_FACTOR;

  if (benchmark.signed) {
    return [
      {
        leftPct: 50 - (benchmark.concerning / axisMax) * 50,
        label: '−' + benchmark.concerning + 'd',
      },
      {
        leftPct: 50 - (benchmark.industryMedian / axisMax) * 50,
        label: '−' + benchmark.industryMedian + 'd',
      },
      { leftPct: 50, label: '0d' },
      {
        leftPct: 50 + (benchmark.industryMedian / axisMax) * 50,
        label: '+' + benchmark.industryMedian + 'd',
      },
      {
        leftPct: 50 + (benchmark.concerning / axisMax) * 50,
        label: '+' + benchmark.concerning + 'd',
      },
    ];
  }

  return [
    {
      leftPct: (benchmark.bestInClass / axisMax) * 100,
      label: benchmark.bestInClass + 'd',
    },
    {
      leftPct: (benchmark.industryMedian / axisMax) * 100,
      label: benchmark.industryMedian + 'd',
    },
    {
      leftPct: (benchmark.concerning / axisMax) * 100,
      label: benchmark.concerning + 'd',
    },
  ];
}

interface ZoneClassification {
  label: string;
  colorToken: string;
}

// Classify the org's value into a qualitative zone label, used as the
// header inside the hover tooltip. Mirrors the marker-color zones so
// the visual and the verbal classification align.
function classifyZone(
  value: number,
  benchmark: SegmentBenchmark
): ZoneClassification {
  if (!benchmark.signed && value < 0) {
    return { label: 'Early', colorToken: 'text.secondary' };
  }
  const magnitude = benchmark.signed ? Math.abs(value) : value;
  if (magnitude <= benchmark.bestInClass) {
    return { label: 'Best in class', colorToken: 'success.main' };
  }
  if (magnitude <= benchmark.industryMedian) {
    return { label: 'Better than industry median', colorToken: 'text.primary' };
  }
  if (magnitude <= benchmark.concerning) {
    return { label: 'Above industry median', colorToken: 'warning.main' };
  }
  return { label: 'Concerning', colorToken: 'error.main' };
}

// Full-scale visualization rendered inside the tooltip — the same
// pointer + ticks + tick-label treatment that used to live on the
// card. Kept in a paper-styled tooltip so the existing palette tokens
// read correctly without re-coloring for a dark surface.
function BenchmarkScale(props: {
  value: number | null;
  benchmark: SegmentBenchmark;
}) {
  const { value, benchmark } = props;
  const ticks = computeAnchorTicks(benchmark);
  const pointer =
    value === null ? null : computePointerPosition(value, benchmark);
  const pointerColor =
    value === null ? null : resolvePointerColor(value, benchmark);
  const isEarlyOnOneSided = value !== null && !benchmark.signed && value < 0;

  return (
    <Box
      sx={{
        position: 'relative',
        height: TOOLTIP_SCALE_HEIGHT_PX,
        width: '100%',
      }}
    >
      {/* Pointer band (sits above the track) */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: TOOLTIP_POINTER_HEIGHT_PX,
        }}
      >
        {pointer !== null &&
          pointerColor !== null &&
          pointer.kind === 'inside' && (
            <Box
              data-testid="benchmark-tooltip-pointer"
              data-color={pointerColor}
              data-left-pct={pointer.leftPct}
              style={{ left: `${pointer.leftPct}%` }}
              sx={{
                position: 'absolute',
                top: 0,
                height: TOOLTIP_POINTER_HEIGHT_PX,
                width: TOOLTIP_POINTER_WIDTH_PX,
                bgcolor: pointerColor,
                transform: 'translateX(-50%)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  width: TOOLTIP_POINTER_DOT_DIAMETER_PX,
                  height: TOOLTIP_POINTER_DOT_DIAMETER_PX,
                  borderRadius: '50%',
                  bgcolor: pointerColor,
                  transform: 'translate(-50%, -25%)',
                }}
              />
            </Box>
          )}
        {pointer !== null &&
          pointerColor !== null &&
          (pointer.kind === 'pinned-left' ||
            pointer.kind === 'pinned-right') && (
            <Box
              data-testid="benchmark-tooltip-pointer-pinned"
              data-pin-side={pointer.kind === 'pinned-left' ? 'left' : 'right'}
              data-color={pointerColor}
              sx={{
                position: 'absolute',
                ...(pointer.kind === 'pinned-left'
                  ? { left: `${PINNED_INSET_PCT}%` }
                  : { right: `${PINNED_INSET_PCT}%` }),
                top: 0,
                height: TOOLTIP_POINTER_HEIGHT_PX,
                color: pointerColor,
                fontSize: TOOLTIP_PINNED_CARET_FONT_PX,
                fontWeight: 700,
                lineHeight: `${TOOLTIP_POINTER_HEIGHT_PX}px`,
              }}
            >
              {pointer.kind === 'pinned-left' ? '‹' : '›'}
            </Box>
          )}
      </Box>

      {/* Track + anchor ticks */}
      <Box
        sx={{
          position: 'absolute',
          top: TOOLTIP_POINTER_HEIGHT_PX + TOOLTIP_TRACK_TOP_GAP_PX,
          left: 0,
          right: 0,
          height: TOOLTIP_TICK_HEIGHT_PX,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: (TOOLTIP_TICK_HEIGHT_PX - TOOLTIP_TRACK_HEIGHT_PX) / 2,
            left: 0,
            right: 0,
            height: TOOLTIP_TRACK_HEIGHT_PX,
            bgcolor: 'divider',
            borderRadius: TOOLTIP_TRACK_HEIGHT_PX / 2,
          }}
        />
        {ticks.map((tick) => (
          <Box
            key={tick.label}
            data-testid="benchmark-tick"
            sx={{
              position: 'absolute',
              top: 0,
              left: `${tick.leftPct}%`,
              width: TOOLTIP_TICK_WIDTH_PX,
              height: TOOLTIP_TICK_HEIGHT_PX,
              bgcolor: 'text.secondary',
              transform: 'translateX(-50%)',
            }}
          />
        ))}
      </Box>

      {/* Tick labels (below the track) */}
      <Box
        sx={{
          position: 'absolute',
          top:
            TOOLTIP_POINTER_HEIGHT_PX +
            TOOLTIP_TRACK_TOP_GAP_PX +
            TOOLTIP_TICK_HEIGHT_PX +
            TOOLTIP_TICK_LABEL_TOP_GAP_PX,
          left: 0,
          right: 0,
          height: TOOLTIP_TICK_LABEL_BAND_HEIGHT_PX,
        }}
      >
        {ticks.map((tick) => (
          <Typography
            key={tick.label}
            variant="caption"
            component="span"
            sx={{
              position: 'absolute',
              left: `${tick.leftPct}%`,
              transform: 'translateX(-50%)',
              color: 'text.secondary',
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {tick.label}
          </Typography>
        ))}
        {isEarlyOnOneSided && value !== null && (
          <Typography
            variant="caption"
            component="span"
            data-testid="benchmark-early-annotation"
            sx={{
              position: 'absolute',
              left: 0,
              top:
                -TOOLTIP_POINTER_HEIGHT_PX - TOOLTIP_EARLY_ANNOTATION_OFFSET_PX,
              color: 'text.secondary',
              lineHeight: 1,
              fontStyle: 'italic',
            }}
          >
            early {formatDays(value)}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// Tooltip body. Renders the qualitative zone classification + the
// org's value, the visual scale, and the labeled anchor table. No
// source citation — provenance lives in the benchmarks data file.
export function BenchmarkTooltipContent(props: {
  value: number | null;
  benchmark: SegmentBenchmark;
}) {
  const { value, benchmark } = props;
  const zone = value === null ? null : classifyZone(value, benchmark);
  const formatAnchor = (val: number) =>
    benchmark.signed ? `±${val}d` : `${val}d`;

  return (
    <Stack spacing={1.25} sx={{ minWidth: TOOLTIP_MIN_WIDTH_PX, p: 0.5 }}>
      {value !== null && zone !== null && (
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          data-testid="benchmark-zone-header"
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: zone.colorToken }}
          >
            {zone.label}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {formatDays(value)}
          </Typography>
        </Stack>
      )}

      <BenchmarkScale value={value} benchmark={benchmark} />

      <Stack spacing={0.25}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption">Best in class</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {formatAnchor(benchmark.bestInClass)}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption">Industry median</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {formatAnchor(benchmark.industryMedian)}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption">Concerning</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {formatAnchor(benchmark.concerning)}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}

// A compact zone-tinted bar with a triangle marker that places the
// org's average-days value on a published-benchmark scale. Renders
// inside each lifecycle summary card between the primary metric and
// the carrying-cost caption. Hover reveals the full scale + anchors.
export function LifecycleBenchmarkIndicator(
  props: LifecycleBenchmarkIndicatorProps
) {
  const { value, benchmark, isLoading } = props;

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        height={INDICATOR_HEIGHT_PX}
        sx={{ borderRadius: 1, mt: 1.5, mb: 2 }}
        data-testid="benchmark-indicator-skeleton"
      />
    );
  }

  const segments = computeZoneSegments(benchmark);
  const pointer =
    value === null ? null : computePointerPosition(value, benchmark);
  const pointerColor =
    value === null ? null : resolvePointerColor(value, benchmark);

  return (
    <Tooltip
      arrow
      placement="bottom"
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: 8,
            maxWidth: TOOLTIP_MAX_WIDTH_PX,
            border: 1,
            borderColor: 'divider',
          },
        },
        arrow: {
          sx: { color: 'background.paper' },
        },
      }}
      title={<BenchmarkTooltipContent value={value} benchmark={benchmark} />}
    >
      <Box
        sx={{
          height: INDICATOR_HEIGHT_PX,
          mt: 1.5,
          mb: 2,
          position: 'relative',
          cursor: 'help',
        }}
        data-testid="benchmark-indicator"
      >
        {pointer !== null &&
          pointerColor !== null &&
          pointer.kind === 'inside' && (
            <Box
              data-testid="benchmark-pointer"
              data-color={pointerColor}
              data-left-pct={pointer.leftPct}
              style={{ left: `${pointer.leftPct}%` }}
              sx={{
                position: 'absolute',
                top: 0,
                width: 0,
                height: 0,
                transform: 'translateX(-50%)',
                borderLeft: `${MARKER_HALF_WIDTH_PX}px solid transparent`,
                borderRight: `${MARKER_HALF_WIDTH_PX}px solid transparent`,
                borderTopWidth: `${MARKER_HEIGHT_PX}px`,
                borderTopStyle: 'solid',
                borderTopColor: pointerColor,
              }}
            />
          )}
        {pointer !== null &&
          pointerColor !== null &&
          (pointer.kind === 'pinned-left' ||
            pointer.kind === 'pinned-right') && (
            <Box
              data-testid="benchmark-pointer-pinned"
              data-pin-side={pointer.kind === 'pinned-left' ? 'left' : 'right'}
              data-color={pointerColor}
              sx={{
                position: 'absolute',
                ...(pointer.kind === 'pinned-left'
                  ? { left: `${PINNED_INSET_PCT}%` }
                  : { right: `${PINNED_INSET_PCT}%` }),
                top: 0,
                fontSize: CARD_PINNED_CARET_FONT_PX,
                fontWeight: 700,
                lineHeight: `${MARKER_HEIGHT_PX + MARKER_GAP_PX}px`,
                color: pointerColor,
              }}
            >
              {pointer.kind === 'pinned-left' ? '‹' : '›'}
            </Box>
          )}

        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: BAR_HEIGHT_PX,
            borderRadius: BAR_HEIGHT_PX / 2,
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          {segments.map((segment, idx) => (
            <Box
              key={`${segment.paletteKey}-${idx}`}
              data-testid="benchmark-zone-segment"
              data-zone={segment.paletteKey}
              sx={{
                width: `${segment.rightPct - segment.leftPct}%`,
                height: '100%',
                bgcolor: (theme) =>
                  segment.paletteKey === 'neutral'
                    ? theme.palette.divider
                    : alpha(
                        theme.palette[segment.paletteKey].main,
                        ZONE_FILL_ALPHA
                      ),
              }}
            />
          ))}
        </Box>
      </Box>
    </Tooltip>
  );
}
